import React from 'react';
import * as LucideIcons from 'lucide-react';
import { HelpCircle, LucideProps } from 'lucide-react';

interface IconProps extends Omit<LucideProps, 'size'> {
    name: string | keyof typeof LucideIcons;
    size?: number;
    color?: string;
    className?: string;
    strokeWidth?: number;
}

function Icon({
    name,
    size = 24,
    color = "currentColor",
    className = "",
    strokeWidth = 2,
    ...props
}: IconProps) {
    // Handle both kebab-case and PascalCase icon names
    let formattedName: string;
    if (typeof name === 'string') {
        // If already PascalCase (starts with uppercase), use as-is
        if (name.charAt(0) === name.charAt(0).toUpperCase() && !name.includes('-')) {
            formattedName = name;
        } else {
            // Convert kebab-case to PascalCase
            formattedName = name.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('');
        }
    } else {
        formattedName = name;
    }
    
    const IconComponent = LucideIcons[formattedName as keyof typeof LucideIcons] as React.ComponentType<LucideProps>;

    if (!IconComponent) {
        return (
            <HelpCircle
                size={size}
                color="gray"
                strokeWidth={strokeWidth}
                className={className}
                {...props}
            />
        );
    }

    // Wrap in error boundary to catch SVG rendering errors
    try {
        return (
            <IconComponent
                size={size}
                color={color}
                strokeWidth={strokeWidth}
                className={className}
                {...props}
            />
        );
    } catch (error) {
        // Fallback to HelpCircle if icon rendering fails
        console.warn(`Failed to render icon: ${formattedName}`, error);
        return (
            <HelpCircle
                size={size}
                color="gray"
                strokeWidth={strokeWidth}
                className={className}
                {...props}
            />
        );
    }
}

export default Icon;